import React from 'react';
import { Separator, Stack } from '@fluentui/react';
import { DialogCardError } from 'common/components/Cards/DialogCard/DialogCardError';
import { usePanelMobileView } from 'common/components/Panel/usePanelMobileView';
import { ModalShimmer } from 'common/components/Shimmer/DefaultShimmers/ModalShimmer';
import { Text } from 'common/components/Text';
import { pageDataService } from 'common/utilities/PageDataService';
import { useLocalizedStrings } from 'i18n';
import { isApiCallComplete, isApiCallFailed } from 'store/apiCallResultStatus';
import { useNotificationsDispatch, useNotificationSettingsSummary } from 'store/notifications/useNotificationSettings';

import { stackTokens, useNotificationSettingsStyles } from '../NotificationSettings.styles';
import { NotificationSettingsSummary } from '../NotificationSettingsSummary';

import { EmailNotificationSettingsSummary } from './EmailNotificationSettingsSummary';

export const EmailNotificationSettings = () => {
  const { t } = useLocalizedStrings();
  const { userPrincipalName } = pageDataService.getUserData();
  const { notificationSettingsSummary, notificationSettingsSummaryLoadStatus } = useNotificationSettingsSummary();
  const { updateSelfNotificationTopicSettings } = useNotificationsDispatch();
  const { isMobileView } = usePanelMobileView();
  const classNames = useNotificationSettingsStyles();

  const onToggleDelegate = React.useCallback(
    (topicId: string) => (checked?: boolean) => {
      updateSelfNotificationTopicSettings({ channel: 'Email', topicId, isEnabled: !!checked });
    },
    [updateSelfNotificationTopicSettings]
  );

  const isLoading = !isApiCallComplete(notificationSettingsSummaryLoadStatus);
  if (isLoading) {
    return <ModalShimmer />;
  }
  if (isApiCallFailed(notificationSettingsSummaryLoadStatus)) {
    return (
      <DialogCardError
        title={t('OfficeShellPanel.NotificationSettings.Error.Title')}
        subtitle={t('OfficeShellPanel.NotificationSettings.Error.Subtitle')}
      />
    );
  }

  return (
    <Stack tokens={isMobileView ? stackTokens.gapXXL : stackTokens.gapMD}>
      <EmailNotificationSettingsSummary
        title={isMobileView ? t('OfficeShellPanel.NotificationSettings.Sections.Email.Title') : undefined}
        text={
          isMobileView
            ? t('OfficeShellPanel.NotificationSettings.Sections.Email.DrillDownText', {
                emailAddress: userPrincipalName,
              })
            : undefined
        }
        iconComponent={undefined}
      />
      {!isMobileView && <Separator className={classNames.noPadding} />}
      <Stack tokens={isMobileView ? undefined : stackTokens.gapSM}>
        {!isMobileView && (
          <Text block>
            {t('OfficeShellPanel.NotificationSettings.Sections.Email.DrillDownText', {
              emailAddress: userPrincipalName,
            })}
          </Text>
        )}
        {notificationSettingsSummary!.emailNotifications.map((topicSetting) => (
          <NotificationSettingsSummary
            key={topicSetting.topicId}
            className={classNames.notificationSetting}
            notificationType="Email"
            telemetryProps={{ topicId: topicSetting.topicId }}
            title={isMobileView ? topicSetting.topicName : undefined}
            toggleNotificationLabel={isMobileView ? '' : topicSetting.topicName}
            toggleAriaLabel={topicSetting.topicName}
            notificationsEnabled={topicSetting.isEnabled ?? false}
            onToggleNotification={onToggleDelegate(topicSetting.topicId)}
            disabledReason={topicSetting.disabledReason}
          />
        ))}
      </Stack>
    </Stack>
  );
};
